import React, { useState } from "react";
import { X, AlertCircle, Trash2, Edit2 } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePaymentMethod,
  selectDeleteStatus,
} from "../../../RTK/Payment-methods/GetAllPaymentMMethods";
import UpdatePaymentMethodModal from "./UpdatePaymentMethodModal ";

const PaymentMethodModal = ({ method, onClose }) => {
  const dispatch = useDispatch();
  const deleteStatus = useSelector(selectDeleteStatus);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);

  if (!method) return null;

  const handleDelete = async () => {
    try {
      await dispatch(deletePaymentMethod(method.id)).unwrap();
      onClose();
    } catch (error) {
      console.error("Failed to delete:", error);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
      dir="rtl"
    >
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl w-full max-w-2xl">
        {showDeleteConfirm ? (
          <div className="p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-gray-700 pb-6">
              <div className="flex items-center gap-3 text-red-400">
                <AlertCircle className="w-6 h-6" />
                <h3 className="text-xl font-semibold">تأكيد الحذف</h3>
              </div>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
              >
                <X className="w-6 h-6 text-gray-400" />
              </button>
            </div>

            <div className="text-center space-y-4">
              <p className="text-lg text-gray-300">
                هل أنت متأكد من حذف طريقة الدفع "{method.name}"؟
              </p>
              <p className="text-sm text-gray-400">
                لا يمكن التراجع عن هذا الإجراء بعد تنفيذه.
              </p>
            </div>

            <div className="flex gap-3 justify-center pt-4">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-6 py-2 rounded-xl bg-gray-700 text-white hover:bg-gray-600 transition-colors"
              >
                إلغاء
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteStatus === "loading"}
                className="px-6 py-2 rounded-xl bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                {deleteStatus === "loading" ? "جاري الحذف..." : "تأكيد الحذف"}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="p-6 border-b border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-xl bg-gray-700/50 p-3">
                    <img
                      src={method.provider_image}
                      alt={method.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">
                      {method.name}
                    </h2>
                    <p className="text-gray-400">{method.provider}</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-700 rounded-xl transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">نسبة الرسوم</p>
                  <p className="text-xl font-bold text-white">
                    {method.fee_percentage}%
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">الحد الأدنى للإيداع</p>
                  <p className="text-xl font-bold text-white">
                    ${method.minimum_deposit_amount}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">سعر العملة</p>
                  <p className="text-xl font-bold text-white">
                    ${method.currency_price}
                  </p>
                </div>
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400">نوع الحساب</p>
                  <p className="text-xl font-bold text-white">
                    {method.payment_method_account_type}
                  </p>
                </div>
              </div>

              {method.description && (
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400 mb-2">الوصف</p>
                  <p className="text-white">{method.description}</p>
                </div>
              )}

              {method.payment_method_info && (
                <div className="bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <p className="text-sm text-gray-400 mb-2">معلومات إضافية</p>
                  <p className="text-white">{method.payment_method_info}</p>
                </div>
              )}

              <div className="flex justify-between items-center">
                <div className="flex gap-3">
                  <span
                    className={`px-4 py-2 rounded-xl text-sm ${
                      method.is_active
                        ? "bg-green-500/20 text-green-400"
                        : "bg-yellow-500/20 text-yellow-400"
                    }`}
                  >
                    {method.is_active ? "نشط" : "غير نشط"}
                  </span>
                  <span
                    className={`px-4 py-2 rounded-xl text-sm ${
                      method.is_automatic
                        ? "bg-blue-500/20 text-blue-400"
                        : "bg-purple-500/20 text-purple-400"
                    }`}
                  >
                    {method.is_automatic ? "تلقائي" : "يدوي"}
                  </span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowUpdateModal(true)}
                    className="px-4 py-2 rounded-xl bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-colors flex items-center gap-2"
                  >
                    <Edit2 className="w-4 h-4" />
                    تحديث
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="px-4 py-2 rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    حذف
                  </button>
                </div>
              </div>
            </div>

            {/* Update Modal */}
            <UpdatePaymentMethodModal
              method={method}
              isOpen={showUpdateModal}
              onClose={() => {
                setShowUpdateModal(false);
                onClose(); // Close both modals after successful update
              }}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodModal;
